<template>
  <VueFinalModal v-model="isOpen" v-bind="$attrs" :z-index-base="100000" esc-to-close>
    <div class="fixed right-0 flex size-full max-w-screen-xs flex-col overflow-auto bg-background px-5 py-8 sm:px-10">
      <div class="flex items-center justify-between border-b border-border pb-8">
        <div class="text-h3">{{ t['w.module.filter_bar.filter_and_order']() }}</div>
        <button
          class="-m-2 flex size-10 items-center justify-center rounded-full transition-colors hover:bg-background-none-hover active:bg-background-none-active"
          @click="isOpen = false"
        >
          <span class="sr-only">{{ t['w.modal.close']() }}</span>
          <Icon class="size-6" name="close" />
        </button>
      </div>
      <div class="min-h-48 grow overflow-auto py-2">
        <AccordionRoot type="multiple" collapsible as-child>
          <ul>
            <FilterAccordion :index="-1" :name="orderByLabel">
              <FilterOrderBy v-model="orderByComputed" :options="props.orderByOptions" class="pb-6" />
            </FilterAccordion>
            <template v-for="(filter, index) of props.filters" :key="`filter-${filter.id}`">
              <FilterAccordion v-slot="slotProps" :index="index" :name="filter.name" :sub-name="getSubName(filter)">
                <FilterCheckbox
                  v-if="filter.type === 'choice'"
                  class="pb-6"
                  :group="filter.id"
                  :title="filter.name"
                  :options="filter.properties.options"
                  :applied-filters="(props.appliedFilters[filter.id] as Choices | undefined) ?? { choices: [] }"
                  @collapse-toggled="
                    (_isCollapsed, newHeight) => {
                      slotProps.updateHeight(newHeight)
                    }
                  "
                  @update-choices="(group: number, choices: number[]) => emit('updateChoices', group, choices)"
                />
                <FilterRange
                  v-else-if="filter.type === 'range'"
                  class="pb-8"
                  :group="filter.id"
                  :title="filter.name"
                  :min="filter.properties.min"
                  :max="filter.properties.max"
                  :applied-filters="(props.appliedFilters[filter.id] as Range | undefined) ?? { min: filter.properties.min, max: filter.properties.max }"
                  :interval="filter.properties.interval"
                  :is-fixed="false"
                  @update-range="(group: number, min: number, max: number) => emit('updateRange', group, min, max)"
                />
              </FilterAccordion>
            </template>
          </ul>
        </AccordionRoot>
      </div>
      <div class="flex items-center justify-between border-t border-border pt-8">
        <Button class="btn-medium btn-rounded shrink-0 px-6 py-3 text-button-xl md:px-8" @click="emit('filterClear')">
          {{ t['w.module.filter_bar.remove_all']() }}
        </Button>
        <Button class="btn-rounded shrink-0 px-6 py-3 text-button-xl md:px-8" @click="isOpen = false">{{ t['w.module.filter_bar.apply']() }}</Button>
      </div>
    </div>
  </VueFinalModal>
</template>

<script setup lang="ts">
import * as t from '~/messages'

import Icon from '~/components/icon/Icon.vue'
import FilterOrderBy from '~/components/filter-bar/FilterOrderBy.vue'
import FilterCheckbox from '~/components/filter-bar/FilterCheckbox.vue'
import FilterAccordion from '~/components/filter-bar/FilterAccordion.vue'
import FilterRange from '~/components/filter-bar/FilterRange.vue'
import type { Filter } from '~/components/filter-bar/filter'
import { VueFinalModal } from 'vue-final-modal'
import { AccordionRoot } from 'reka-ui'
import { useIsFilterOpenState } from '~/composables/states'

const props = defineProps<{
  filters: Filter[]
  appliedFilters: Record<string, AppliedFilter>
  orderByOptions: Record<string, string>
  defaultSortOrder: string
  orderBy: string
}>()

interface Choices {
  choices: number[]
}

interface Range {
  min: number
  max: number
}

type AppliedFilter = Choices | Range

const emit = defineEmits<{
  orderByChanged: [newValue: string]
  updateChoices: [group: number, choices: number[]]
  updateRange: [group: number, min: number, max: number]
  filterClear: []
}>()

const isOpen = useIsFilterOpenState()

const orderByComputed = computed({
  get() {
    return props.orderBy
  },
  set(newValue) {
    emit('orderByChanged', newValue)
  },
})

const orderByLabel = computed(() => {
  return t['w.module.filter_bar.order_by']() + ': ' + props.orderByOptions[orderByComputed.value]
})

const getSubName = (filter: Filter): string => {
  if (filter.type === 'range') {
    const appliedFilters = props.appliedFilters[filter.id] as Range | undefined

    if (!appliedFilters) {
      return ''
    }

    const prefix = filter.properties.prefix ? ' ' + filter.properties.prefix : ''
    const suffix = filter.properties.suffix ? ' ' + filter.properties.suffix : ''

    return prefix + appliedFilters.min + ' - ' + appliedFilters.max + suffix
  } else {
    const appliedFilters = props.appliedFilters[filter.id] as Choices | undefined

    if (!appliedFilters) {
      return ''
    }

    return appliedFilters.choices
      .map((choice) => filter.properties.options.find((option) => option.id === choice))
      .filter((x) => !!x)
      .map((x) => x.name)
      .join(', ')
  }
}
</script>
